import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../baseQuery';
import {
  BeneficiariesTypes,
  BeneficiarTypes,
  BodyUpdateBeneficiarType,
  BodyUpdateCommissionType,
  BodyUpdateLimitType,
  DataPaginationBeneficiarType,
  QueryParamsPaginationBeneficiarType,
  ResponseGetTabTypy,
  TotalBalanceBeneficiaryType,
} from './type';

const getToken = () => localStorage.getItem('token');

const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

export const beneficiariesApi = createApi({
  reducerPath: 'beneficiariesApi',
  tagTypes: ['Beneficiaries', 'Beneficiar', 'Tags', 'Pagination', 'Balance'],
  refetchOnReconnect: true,
  keepUnusedDataFor: 10,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    getBeneficiaries: build.query<BeneficiariesTypes, void>({
      query: () => ({
        url: `/customers`,
        method: 'GET',
      }),
      providesTags: () => ['Beneficiaries'],
    }),
    getBeneficiar: build.query<BeneficiarTypes, { beneficiary_id: number | null }>({
      query: ({ beneficiary_id }) => ({
        url: `/customers/${beneficiary_id}`,
        method: 'GET',
      }),
      providesTags: () => ['Beneficiar'],
    }),
    getTab: build.query<ResponseGetTabTypy, { id: number | null; title: string | null }>({
      query: ({ id, title }) => ({
        url: `/customers/${id}/tabs/${title}`,
        method: 'GET',
      }),
      providesTags: () => ['Tags'],
    }),
    createBeneficiar: build.mutation({
      query: ({ body }) => ({
        url: `/customers/initialize`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Beneficiaries'],
    }),
    updateBeneficiarFeatures: build.mutation<
      any,
      { body: { unitbased: 1 | 0 }; id: number }
    >({
      query: ({ body, id }) => ({
        url: `/customers/${id}/update-features`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Beneficiar', 'Beneficiaries'],
    }),
    getTabPaginationBeneficiar: build.query<
      DataPaginationBeneficiarType,
      QueryParamsPaginationBeneficiarType
    >({
      query: ({ page, per_page, search }) => ({
        url: `/v2/customers`,
        method: 'GET',
        params: { page, per_page, search },
      }),
      providesTags: () => ['Pagination'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const q = (queryArgs.search ?? '').trim();
        const perPage = queryArgs.per_page ?? 20;
        return `${endpointName}(${q})(${perPage})`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if ((arg.page ?? 1) === 1) {
          return newItems;
        }
        if (currentCache?.data && newItems.data) {
          const seen = new Set<number | string | null>();
          const data = [...currentCache.data, ...newItems.data].filter((item) => {
            const key = item.id;
            if (key == null || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          return {
            ...newItems,
            data,
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    getBalanceBeneficiar: build.query<TotalBalanceBeneficiaryType, void>({
      query: () => ({
        url: `/customers/total-balance`,
        method: 'GET',
      }),
      providesTags: () => ['Balance'],
    }),
    updateBeneficiar: build.mutation<
      BeneficiarTypes,
      { id: number | string; body: BodyUpdateBeneficiarType }
    >({
      query: ({ id, body }) => ({
        url: `/customers/${id}/update`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Beneficiar', 'Beneficiaries'],
    }),
    updateBeneficiarCommission: build.mutation<
      BeneficiarTypes,
      { id: number | string; body: BodyUpdateCommissionType }
    >({
      query: ({ id, body }) => ({
        url: `/customers/${id}/change-commission`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Beneficiar', 'Beneficiaries'],
    }),
    updateBeneficiarLimit: build.mutation<
      BeneficiarTypes,
      { id: number | string; body: BodyUpdateLimitType }
    >({
      query: ({ id, body }) => ({
        url: `/customers/${id}/change-order-limits`,
        method: 'POST',
        body,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Beneficiar', 'Beneficiaries'],
    }),
  }),
});

export const {
  useGetTabQuery,
  useGetBeneficiarQuery,
  useUpdateBeneficiarFeaturesMutation,
  useGetBeneficiariesQuery,
  useCreateBeneficiarMutation,
  useGetTabPaginationBeneficiarQuery,
  useGetBalanceBeneficiarQuery,
  useUpdateBeneficiarMutation,
  useUpdateBeneficiarCommissionMutation,
  useUpdateBeneficiarLimitMutation,
} = beneficiariesApi;
